// Stock page pieces: the hero, trade-plan card, plain-English read, levels, events, peers, history stats.
import { Link } from '@/lib/nav';
import { NSE, NSE_NAMES, sessionPhase, zoneState, type Analysed, type DailyCandle, type Detail, type Snapshot, type ZoneState } from '@planb/core';
import { fmtPct } from '@/charts/board';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useState } from 'react';
import { NotebookPenIcon } from 'lucide-react';
import { toast } from 'sonner';
import { logTrade } from '@/lib/client/use-trades';
import { Button } from '@/components/ui/button';
import { SWING } from '@/lib/levels';
import { PositionSize } from './position-size';
import { TradeForm, type TradeDraft } from './trade-form';
import { Change, Initials, KV, RangeBar, SideTag, Sparkline, Stat, StrengthBar } from './market-ui';

const fmtPx = NSE.formatPrice;
const cur = NSE.currencySymbol;

export function StockHero({ a, rank, time, aside, actions }: { a: Analysed; rank: number; time: string; aside?: React.ReactNode; actions?: React.ReactNode }) {
  const chg = a.ltp - a.prevClose;
  const name = NSE_NAMES[a.symbol];
  const move = <span className={cn('num text-sm font-medium', a.bull ? 'text-bull' : 'text-bear')}>{chg >= 0 ? '+' : ''}{fmtPx(chg)} ({fmtPct(a.pct)})</span>;
  const tags = (
    <>
      <SideTag bull={a.bull} className="h-6 flex-none px-2.5" />
      <Chip>Strength <b className="num text-ink">{a.score}</b>/100</Chip>
      {rank > 0 && <Chip>#{rank} {a.bull ? 'bullish' : 'bearish'}</Chip>}
      <Chip>{a.withSector ? 'Moving with its sector' : 'Moving alone'}</Chip>
      {a.hasOI && <Chip>{a.buildUp}</Chip>}
      {a.thin && <span title="A usual 5-minute candle trades under ₹40 lakh (last 50 sessions): one order can move a candle, so zones and levels are less reliable."
        className="inline-flex h-6 flex-none items-center rounded-full bg-warn-soft px-2.5 text-xs font-medium text-warn-ink">Thin trading</span>}
    </>
  );
  return (
    <div>
      {/* Phones: one compact block (name row with the actions, price with the replay, tags in one scrolling row), so the chart starts high. */}
      <div className="sm:hidden">
        <div className="flex items-center gap-2.5">
          <Initials symbol={a.symbol} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[11px] tracking-wide text-muted-foreground">{a.symbol} · NSE · {a.sector}</p>
            <h1 className="truncate text-lg leading-tight font-semibold">{name ?? a.symbol}</h1>
          </div>
          {actions && <div className="flex flex-none gap-1.5">{actions}</div>}
        </div>
        <div className="mt-2 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-heading text-[24px] leading-tight font-semibold tracking-tight text-ink">{cur}{fmtPx(a.ltp)}</span>
              {move}
            </div>
            <p className="truncate text-[11px] text-muted-foreground/80">{time}</p>
          </div>
          {aside && <div className="flex-none">{aside}</div>}
        </div>
        <div className="-mx-4 mt-2.5 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none]">{tags}</div>
      </div>
      <div className="max-sm:hidden">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Initials symbol={a.symbol} size="lg" />
          <div className="min-w-40 flex-1">
            <p className="truncate text-[12.5px] tracking-wide text-muted-foreground">{a.symbol} · NSE · {a.sector}</p>
            <h1 id="detailTitle" className="truncate text-2xl font-semibold">{name ?? a.symbol}</h1>
          </div>
          <div className="text-right">
            <div className="font-heading text-[28px] leading-tight font-semibold tracking-tight text-ink">{cur}{fmtPx(a.ltp)}</div>
            <div className="flex flex-wrap items-baseline justify-end gap-x-2">
              {move}
              <span className="text-xs text-muted-foreground/80">{time}</span>
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {tags}
          {aside && <div className="ml-auto">{aside}</div>}
        </div>
      </div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex h-6 flex-none items-center gap-1 rounded-full border bg-card px-2.5 text-xs whitespace-nowrap text-muted-foreground">{children}</span>;
}

export const STATE_TEXT: Record<ZoneState, { label: string; note: string; cls: string }> = {
  fresh: { label: 'Fresh zone', note: 'Price hasn’t come back since the zone was confirmed. The first return is usually the cleanest.', cls: 'bg-bull-soft text-bull-strong' },
  testing: { label: 'Testing', note: 'Price is back in the zone; whether it reacts isn’t decided yet (it has the touch candle and the next three).', cls: 'bg-brand-soft text-brand-strong' },
  reacted: { label: 'Reacted', note: 'On its last visit price closed at least half a usual candle away from the zone within three candles.', cls: 'bg-bull-soft text-bull-strong' },
  failed: { label: 'No reaction', note: 'On its last visit price didn’t move away within three candles. The zone still stands, but the orders there looked thin.', cls: 'bg-warn-soft text-warn-ink' },
  broken: { label: 'Broken', note: 'A candle closed beyond the far edge.', cls: 'bg-muted text-muted-foreground' },
};

/** "Reacted", "No reaction · 3rd visit", "Fresh zone"… */
export const zoneStateLabel = (st: ZoneState, visits: number) =>
  `${STATE_TEXT[st].label}${visits > 1 && st !== 'broken' ? ` · ${visits === 2 ? '2nd' : visits === 3 ? '3rd' : `${visits}th`} visit` : ''}`;

const rrClass = (rr: number | null) => (rr === null ? 'text-muted-foreground' : rr >= 2 ? 'text-bull' : rr < 1 ? 'text-bear' : undefined);
/** Very large ratios come from tight zones far from the target; above 5 the exact figure means little. */
const rrText = (rr: number | null) => (rr === null ? '— (no real target ahead)' : rr >= 5 ? '1 : 5+' : `1 : ${rr.toFixed(1)}`);

/** Where Groww puts Buy/Sell: the setup at the zone, the same idea acted on now, and the risk. */
export function TradePlan({ a, d, time, children }: { a: Analysed; d: Detail; time?: string; children: React.ReactNode }) {
  const p = d.plan;
  const [logging, setLogging] = useState(false);
  // The trade form starts from the zone plan when there is one, else from acting now.
  const zoneOk = !!p && !a.eventRisk;
  const draft: TradeDraft = {
    symbol: a.symbol, side: a.bull ? 'long' : 'short',
    entry: zoneOk ? p!.z : a.ltp, stop: zoneOk ? p!.stop : d.stop, target: zoneOk ? p!.t1 : d.target, setup: zoneOk ? 'order-block' : '',
    plan: {
      time, side: a.bull ? 'bull' : 'bear', score: a.score, eventRisk: a.eventRisk,
      ...(p ? { zone: zoneStateLabel(p.state, p.retests).toLowerCase(), state: p.state, z: p.z, stop: p.stop, t1: p.t1, t2: p.t2, rr: p.rr } : {}),
      ...(time ? { session: sessionPhase(Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5))).id } : {}),
    },
  };
  const targetPct = Math.abs(((d.target - a.ltp) / a.ltp) * 100);
  const side = a.bull;
  return (
    <Card className="gap-0 py-0">
      <CardHeader className="border-b py-4">
        <CardTitle className="text-[15px] font-semibold">Trade plan</CardTitle>
        <p className="text-xs text-muted-foreground">If the idea is “{side ? 'buyers stay in control' : 'sellers stay in control'}”</p>
      </CardHeader>
      <CardContent className="py-4">
        {a.eventRisk && (
          <div className="mb-4 rounded-lg border border-warn-line bg-warn-soft px-3 py-2.5 text-[12.5px] leading-snug text-warn-ink">
            <b>Event risk.</b> {a.eventRisk}. Zone levels are withheld: moves like this don’t respect them.
          </div>
        )}

        {p ? (
          <section aria-label="If you wait for the zone">
            <div className="mb-1 flex items-center justify-between gap-2">
              <h3 className="text-[13px] font-semibold">If you wait for the zone</h3>
              <span className={cn('inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold', STATE_TEXT[p.state].cls)}>
                {zoneStateLabel(p.state, p.retests)}
              </span>
            </div>
            <KV label="Z · zone" value={`${cur}${fmtPx(p.z)}`} />
            <KV label="Stop · wrong past" value={`${cur}${fmtPx(p.stop)}`} className="text-warn" />
            <KV label={`T1 · ${p.t1Name}`} value={`${cur}${fmtPx(p.t1)}`} className={side ? 'text-bull' : 'text-bear'} />
            <KV label={`T2 · ${p.t2Name}`} value={`${cur}${fmtPx(p.t2)}`} className={side ? 'text-bull' : 'text-bear'} />
            <KV label="Risk per share" value={`${cur}${fmtPx(p.riskPerShare)}`} />
            <KV label={`Reward : risk to ${p.rrTo.toUpperCase()}`} value={rrText(p.rr)} className={rrClass(p.rr)} />
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {p.inZone ? 'Price is inside the zone now.' : `Price is ${Math.abs(p.distPct).toFixed(2)}% ${p.distPct >= 0 ? 'above' : 'below'} the zone; this only applies if it comes back.`}{' '}
              {STATE_TEXT[p.state].note} The stop sits just beyond the zone, and at least one average candle from Z.
              {p.rr !== null && p.rrTo === 't1' && ' There is no real level beyond T1 yet, so T2 is projected and reward:risk is measured to T1.'}
            </p>
          </section>
        ) : !a.eventRisk && (
          <p className="text-xs leading-relaxed text-muted-foreground">No active {a.bull ? 'bullish' : 'bearish'} order-block zone right now, so there is no zone plan.</p>
        )}

        <section aria-label="If you act now" className={cn(p || a.eventRisk ? 'mt-5 border-t pt-4' : 'mt-4')}>
          <h3 className="mb-1 text-[13px] font-semibold">If you act now</h3>
          <KV label="Last price" value={`${cur}${fmtPx(a.ltp)}`} />
          <KV label={`Wrong past (${a.bull ? SWING.S1 : SWING.R1})`} value={`${cur}${fmtPx(d.stop)}`} className="text-warn" />
          <KV label={`Target (${a.bull ? SWING.R2 : SWING.S2})`} value={`${cur}${fmtPx(d.target)}`} className={a.bull ? 'text-bull' : 'text-bear'} />
          <KV label="Risk · room" value={`${d.risk.toFixed(2)}% · ${targetPct.toFixed(2)}%`} />
          <KV label="Reward : risk" value={rrText(d.rr)} className={rrClass(d.rr)} />
        </section>

        <PositionSize cases={[
          ...(p && !a.eventRisk ? [{ label: 'At the zone', entry: p.z, riskPerShare: p.riskPerShare }] : []),
          { label: 'Now', entry: a.ltp, riskPerShare: Math.abs(a.ltp - d.stop) },
        ]} />

        <p className="my-4 text-xs leading-relaxed text-muted-foreground">
          Reward:risk above 1:2 means the possible move is at least twice the possible loss; below 1:1 the room left is smaller than the risk.
          The two plans use different stops and targets, so their ratios aren’t directly comparable.
        </p>
        <Button variant="outline" className="mb-4 w-full rounded-full" onClick={() => setLogging(true)}><NotebookPenIcon />Log this trade in my journal</Button>
        {children}
        <p className="mt-4 text-[11px] text-muted-foreground/80">For learning only, not investment advice.</p>
        <TradeForm open={logging} onOpenChange={setLogging} draft={draft}
          onSave={async (t) => { const e = await logTrade(t); if (!e) toast.success('Logged in your journal'); return e; }} />
      </CardContent>
    </Card>
  );
}

export function KeyNumbers({ a, d }: { a: Analysed; d: Detail }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-x-5 gap-y-4">
      <Stat label="Open" value={fmtPx(a.open)} />
      <Stat label="Prev. close" value={fmtPx(a.prevClose)} />
      <Stat label="VWAP" value={fmtPx(a.vwap)} />
      <Stat label="Volume vs normal" value={`${a.relVol.toFixed(2)}×`} />
      {a.moveRatio !== null && <Stat label="Move vs usual day" value={`${a.moveRatio.toFixed(2)}× range`} />}
      <Stat label="Strength percentile" value={`P${d.percentile.toFixed(1)}`} />
      {a.flow && <Stat label="Order flow (est.)" value={`${a.flow.deltaPct >= 0 ? '+' : '−'}${Math.abs(a.flow.deltaPct).toFixed(0)}% ${a.flow.deltaPct >= 0 ? 'buy' : 'sell'}`} className={a.flow.deltaPct >= 0 ? 'text-bull' : 'text-bear'} />}
      {a.flow?.bidPct != null && <Stat label="Order book" value={`${a.flow.bidPct.toFixed(0)}% bids`} />}
      <Stat label="Open interest" value={a.hasOI ? fmtPct(a.oiChg) : '—'} />
      <div className="col-span-full"><div className="mb-1.5 text-xs text-muted-foreground">Today&apos;s range</div>
        <RangeBar lo={a.low} hi={a.high} value={a.ltp} loLabel={fmtPx(a.low)} hiLabel={fmtPx(a.high)} gradient /></div>
    </div>
  );
}

export function Explain({ a, d, t }: { a: Analysed; d: Detail; t: number }) {
  const lv = d.levels;
  const held = Math.round(a.vwapHold * 100);
  const sofar = t < NSE.bars ? ' so far' : '';
  const level = (k: 'S1' | 'R1', kind: 'support' | 'resistance') => {
    const name = kind === 'support' ? 'Nearest support' : 'Nearest resistance';
    return d.projected.has(k)
      ? `${name} ${cur}${fmtPx(lv[k])} is projected: price is at the day's ${kind === 'support' ? 'low' : 'high'}, so there is no earlier swing ${kind === 'support' ? 'below' : 'above'}. It is placed two average 5-minute ranges away.`
      : `${name} ${cur}${fmtPx(lv[k])}: a recent swing ${kind === 'support' ? 'low where buyers stepped in' : 'high where sellers stepped in'}.`;
  };
  const near = d.liquidity.slice().sort((p, q) => Math.abs(p.distPct) - Math.abs(q.distPct)).slice(0, 2);
  const recent = a.sweeps.filter((w) => w.idx >= t - 12);
  const latest = recent[recent.length - 1] || a.sweeps[a.sweeps.length - 1];
  const f = a.flow;
  const Item = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <li className="rounded-lg border border-border/60 bg-tint px-3.5 py-3 text-[13.5px] leading-relaxed"><b className="text-ink">{title}.</b> {children}</li>
  );

  return (
    <ul className="grid gap-2.5">
      <Item title="Trend">Price has been {a.bull ? 'above' : 'below'} VWAP ({cur}{fmtPx(a.vwap)}, the day&apos;s average traded price) for {held}% of the session{sofar}, so {a.bull ? 'buyers' : 'sellers'} have controlled the average price{held >= 70 ? ' most of the time' : held >= 50 ? ' more often than not' : ' only part of the time; the move is less clean'}.{t < 6 ? ' It is early, so this can change quickly.' : ''}</Item>
      <Item title="Zone">{d.zone
        ? <>{cur}{fmtPx(d.zone.bottom)}–{cur}{fmtPx(d.zone.top)} is a {d.zone.type === 'bull' ? 'bullish' : 'bearish'} order block: the last {d.zone.type === 'bull' ? 'red' : 'green'} candle before a sharp push. Large {d.zone.type === 'bull' ? 'buyers' : 'sellers'} may have entered here, so price may react if it returns: on recent NSE data about 4 in 10 first visits did. {zoneStateLabel(zoneState(d.zone), d.zone.retests)}: {STATE_TEXT[zoneState(d.zone)].note}</>
        : <>No active order block yet: the stock hasn&apos;t made a sharp, one-sided push that would leave a footprint.</>}</Item>
      <Item title="Levels">{level('S1', 'support')} {level('R1', 'resistance')}</Item>
      <Item title="Risk">If the idea is “{a.bull ? 'buyers stay in control' : 'sellers stay in control'}”, it is proven wrong past {cur}{fmtPx(d.stop)}, {d.risk.toFixed(2)}% away. The second {a.bull ? 'swing high (SH2)' : 'swing low (SL2)'} is {Math.abs(((d.target - a.ltp) / a.ltp) * 100).toFixed(2)}% away, a reward:risk of 1:{d.rr.toFixed(1)}.</Item>
      <Item title="Sector">{a.sector} is {fmtPct(a.sectorAvg)} with {a.sectorAdv} of {a.sectorN} stocks up, so this stock is moving <b>{a.withSector ? 'with' : 'alone'}</b>{a.withSector ? ' its sector. Moves with sector support tend to be more reliable.' : '. Without its sector behind it, treat the move with more caution.'}</Item>
      {a.moveRatio !== null && <Item title="Size">Today&apos;s {Math.abs(a.pct).toFixed(2)}% is {a.moveRatio.toFixed(2)}× this stock&apos;s usual daily range. {a.moveRatio >= 1 ? 'That is an unusually large day for it.' : a.moveRatio >= 0.5 ? 'A solid but normal-sized day for it.' : 'A small move by its own standards.'}</Item>}
      {near.length > 0 && <Item title="Liquidity">Many stop-loss and breakout orders sit at obvious prices: yesterday&apos;s high/low, the first 15 minutes&apos; range and round numbers. Price is often drawn to them, and a clean break through one on volume matters. Closest now: {near.map((l) => `${l.name} ${cur}${fmtPx(l.p)} (${l.distPct >= 0 ? '+' : ''}${l.distPct.toFixed(2)}%)`).join(' and ')}.</Item>}
      <Item title="Liquidity sweep">{latest ? <SweepText a={a} d={d} w={latest} /> : <>None today: price hasn&apos;t poked past yesterday&apos;s high/low, the opening range or the day&apos;s earlier high/low and snapped back.</>}</Item>
      {f ? (
        <Item title="Order flow (estimated)">About {f.buyShare.toFixed(0)}% of traded volume{f.from > 0 ? ` since ${NSE.timeLabel(f.from)} (when the feed started)` : ' today'} was buyer-initiated, a net {f.deltaPct >= 0 ? '+' : ''}{f.deltaPct.toFixed(0)}%.{' '}
          {(a.bull ? f.deltaPct > 3 : f.deltaPct < -3) ? 'That confirms the move: aggressive orders are pushing the same way as price.'
            : (a.bull ? f.deltaPct < -3 : f.deltaPct > 3) ? <><b>That disagrees with the price move</b>: aggressive orders lean the other way, which can mean large passive orders are absorbing the move. Treat it as fragile.</>
            : 'Buying and selling are roughly balanced.'}
          {f.bidPct !== null ? ` Right now ${f.bidPct.toFixed(0)}% of the quantity waiting in the order book is on the buy side.` : ''} Estimated from ~1/second ticks: each trade&apos;s side is inferred from whether it hit the ask or the bid.</Item>
      ) : (
        <Item title="Order flow">Not available yet. It is measured live from ticks, so it needs the live feed running during market hours; historical candles don&apos;t record which side started each trade.</Item>
      )}
      <Item title="Open interest">{a.hasOI ? `${fmtPct(a.oiChg)} today (${a.buildUp.toLowerCase()}).` : 'No futures contract, so no open-interest signal.'} Volume is {a.relVol.toFixed(2)}× normal for this time of day.</Item>
    </ul>
  );
}

function SweepText({ a, d, w }: { a: Analysed; d: Detail; w: Analysed['sweeps'][number] }) {
  const after = ((a.ltp - d.bars[w.idx].c) / d.bars[w.idx].c) * 100;
  const what = w.type === 'bear'
    ? `ran above ${w.name} ${cur}${fmtPx(w.level)} (to ${cur}${fmtPx(w.extreme)}) and closed back below`
    : `dropped below ${w.name} ${cur}${fmtPx(w.level)} (to ${cur}${fmtPx(w.extreme)}) and closed back above`;
  const vol = w.volMult ? ` on ${w.volMult.toFixed(1)}× recent volume` : '';
  if (w.failed) {
    return <>At {NSE.timeLabel(w.idx)} price {what}{vol}, but at {NSE.timeLabel(w.failIdx!)} it closed through the level again, so the sweep <b>failed</b> and turned into a breakout. A failed sweep often means the other side is strong.</>;
  }
  const meaning = w.type === 'bear'
    ? 'Stop orders above that high were triggered and breakout buyers got trapped, a sign the push higher was rejected.'
    : 'Stop orders below that low were triggered and late sellers got trapped, a sign the push lower was rejected.';
  return <>At {NSE.timeLabel(w.idx)} price {what}{vol}. {meaning} Price is {after >= 0 ? '+' : ''}{after.toFixed(2)}% since.{a.sweeps.length > 1 ? ` (${a.sweeps.length} sweeps today; markers on the chart.)` : ''}</>;
}

const LEVEL_COLOR: Record<string, string> = {
  res: 'text-bull-strong', sup: 'text-bear-strong', vwap: 'text-brand-strong', liq: 'text-liq', zone: 'text-ink', now: 'text-white',
};

export function Levels({ a, d }: { a: Analysed; d: Detail }) {
  const rows = [
    ...(['R2', 'R1'] as const).map((k) => ({ label: SWING[k], p: d.levels[k], kind: 'res', note: d.projected.has(k) ? 'projected' : 'swing high' })),
    { label: 'VWAP', p: d.vwap[d.vwap.length - 1], kind: 'vwap', note: 'average traded price' },
    ...(d.zone ? [{ label: 'Zone', p: (d.zone.top + d.zone.bottom) / 2, kind: 'zone', note: `${d.zone.type === 'bull' ? 'bullish' : 'bearish'} OB ${fmtPx(d.zone.bottom)}–${fmtPx(d.zone.top)}` }] : []),
    ...(['S1', 'S2'] as const).map((k) => ({ label: SWING[k], p: d.levels[k], kind: 'sup', note: d.projected.has(k) ? 'projected' : 'swing low' })),
    ...d.liquidity.map((l) => ({ label: l.key === 'RND' ? 'Round' : l.key, p: l.p, kind: 'liq', note: l.name })),
    { label: 'Price', p: a.ltp, kind: 'now', note: 'last traded' },
  ].sort((x, y) => y.p - x.p);
  return (
    <Table>
      <TableHeader><TableRow className="hover:bg-transparent">
        <TableHead className="pl-5">Level</TableHead><TableHead className="text-right">Price</TableHead>
        <TableHead className="text-right">Distance</TableHead><TableHead className="pr-5">What it is</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map((r, i) => {
        const dist = ((r.p - a.ltp) / a.ltp) * 100;
        const now = r.kind === 'now';
        return (
          <TableRow key={i} className={cn(now && 'bg-ink text-white hover:bg-ink')}>
            <TableCell className={cn('pl-5 font-semibold', LEVEL_COLOR[r.kind])}>{r.label}</TableCell>
            <TableCell className={cn('num text-right', now ? 'text-white' : 'text-ink')}>{cur}{fmtPx(r.p)}</TableCell>
            <TableCell className="num text-right">{now ? '—' : `${dist >= 0 ? '+' : ''}${dist.toFixed(2)}%`}</TableCell>
            <TableCell className={cn('pr-5', !now && 'text-muted-foreground')}>{r.note}</TableCell>
          </TableRow>
        );
      })}</TableBody>
    </Table>
  );
}

const EVENT_COLOR: Record<string, string> = {
  up: 'border-l-bull', down: 'border-l-bear', sweep: 'border-l-warn', hi: 'border-l-bull-strong', lo: 'border-l-bear-strong', muted: 'border-l-border text-muted-foreground',
};

export function Events({ a, d }: { a: Analysed; d: Detail }) {
  const ev: { i: number; kind: string; text: string }[] = [];
  const bars = d.bars;
  if (bars.length) {
    let hiIdx = 0, loIdx = 0;
    bars.forEach((b, i) => { if (b.h > bars[hiIdx].h) hiIdx = i; if (b.l < bars[loIdx].l) loIdx = i; });
    ev.push({ i: hiIdx, kind: 'hi', text: `Day high so far ${cur}${fmtPx(bars[hiIdx].h)}` });
    ev.push({ i: loIdx, kind: 'lo', text: `Day low so far ${cur}${fmtPx(bars[loIdx].l)}` });
  }
  for (let i = 1; i < bars.length; i++) {
    const was = bars[i - 1].c >= d.vwap[i - 1], is = bars[i].c >= d.vwap[i];
    if (was !== is) ev.push({ i, kind: is ? 'up' : 'down', text: `Closed ${is ? 'above' : 'below'} VWAP` });
  }
  a.obs.forEach((o) => {
    ev.push({ i: o.idx, kind: o.type === 'bull' ? 'up' : 'down', text: `${o.type === 'bull' ? 'Bullish' : 'Bearish'} order block formed ${cur}${fmtPx(o.bottom)}–${fmtPx(o.top)}` });
    if (o.mitigated && o.endIdx !== undefined) ev.push({ i: o.endIdx, kind: 'muted', text: `Order block from ${NSE.timeLabel(o.idx)} used up` });
  });
  a.sweeps.forEach((w) => {
    ev.push({ i: w.idx, kind: 'sweep', text: `Swept ${w.name} ${cur}${fmtPx(w.level)} and closed back ${w.type === 'bear' ? 'below' : 'above'}${w.volMult ? ` (${w.volMult.toFixed(1)}× volume)` : ''}` });
    if (w.failed && w.failIdx !== undefined) ev.push({ i: w.failIdx, kind: 'muted', text: `Sweep of ${w.key} failed: closed through it` });
  });
  ev.sort((p, q) => q.i - p.i);
  return (
    <ol className="grid gap-1">
      {ev.length ? ev.slice(0, 24).map((e, k) => (
        <li key={k} className={cn('flex gap-3 border-l-2 py-2 pl-3 text-[13px]', EVENT_COLOR[e.kind])}>
          <time className="num min-w-10 text-xs text-muted-foreground">{NSE.timeLabel(e.i)}</time>{e.text}
        </li>
      )) : <li className="text-sm text-muted-foreground">Nothing notable yet.</li>}
    </ol>
  );
}

export function Peers({ a, snap, href }: { a: Analysed; snap: Snapshot; href: (sym: string) => string }) {
  const peers = snap.stocks.filter((s) => s.sector === a.sector).sort((p, q) => q.pct - p.pct);
  return (
    <Table>
      <TableHeader><TableRow className="hover:bg-transparent">
        <TableHead className="pl-5">Company</TableHead><TableHead className="hidden sm:table-cell">Today</TableHead>
        <TableHead className="text-right">Price</TableHead><TableHead className="hidden w-48 md:table-cell">Day range</TableHead>
        <TableHead className="pr-5">Strength</TableHead>
      </TableRow></TableHeader>
      <TableBody>{peers.map((s) => (
        <TableRow key={s.symbol} className={cn(s.symbol === a.symbol && 'bg-brand-soft hover:bg-brand-soft')}>
          <TableCell className="py-3 pl-5">
            <Link href={href(s.symbol)} className="flex items-center gap-3">
              <Initials symbol={s.symbol} size="sm" />
              <span className="min-w-0">
                <span className="block max-w-48 truncate font-medium text-ink">{NSE_NAMES[s.symbol] ?? s.symbol}</span>
                <span className="block text-xs text-muted-foreground">{s.symbol} · {s.withSector ? 'with sector' : 'alone'}</span>
              </span>
            </Link>
          </TableCell>
          <TableCell className="hidden sm:table-cell"><Sparkline series={s.series} up={s.bull} width={80} /></TableCell>
          <TableCell className="text-right"><div className="num text-[13.5px] text-ink">{fmtPx(s.ltp)}</div><Change pct={s.pct} className="text-xs" /></TableCell>
          <TableCell className="hidden md:table-cell"><RangeBar lo={s.low} hi={s.high} value={s.ltp} /></TableCell>
          <TableCell className="pr-5"><StrengthBar score={s.score} bull={s.bull} /></TableCell>
        </TableRow>
      ))}</TableBody>
    </Table>
  );
}

// usualPct: the 50-session usual day range the engine sizes its candles with, so the page and the
// order blocks quote the same "usual"; the last 20 sessions stand in only when it is missing.
export function HistoryStats({ a, hist, usualPct }: { a: Analysed; hist: DailyCandle[]; usualPct?: number }) {
  if (!hist.length) return null;
  const closes = hist.map((x) => x.c);
  const ranges = hist.map((x) => ((x.h - x.l) / x.c) * 100);
  const avgRange = usualPct ?? ranges.reduce((x, v) => x + v, 0) / ranges.length;
  // Range includes today, so a new high/low reads as 100% / 0% rather than off the scale.
  const hi20 = Math.max(a.high, ...hist.map((x) => x.h)), lo20 = Math.min(a.low, ...hist.map((x) => x.l));
  const newHigh = a.high > Math.max(...hist.map((x) => x.h)), newLow = a.low < Math.min(...hist.map((x) => x.l));
  const ch = (n: number) => (closes.length >= n ? ((a.ltp - closes[closes.length - n]) / closes[closes.length - n]) * 100 : null);
  const c5 = ch(5), c20 = ch(20);
  return (
    <div className="grid gap-4 border-t px-4 pt-4 pb-1">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-x-5 gap-y-3">
        <Stat label="5-day change" value={<Change pct={c5} />} />
        <Stat label="20-day change" value={<Change pct={c20} />} />
        <Stat label={usualPct != null ? 'Usual daily range · 50d' : 'Usual daily range · 20d'} value={`${avgRange.toFixed(2)}%`} />
        <Stat label="Signal" value={newHigh ? 'New 20-day high' : newLow ? 'New 20-day low' : trendNote(a, hist) || '—'} className="font-sans text-[13px]" />
      </div>
      <div><div className="mb-1.5 text-xs text-muted-foreground">20-day range</div>
        <RangeBar lo={lo20} hi={hi20} value={a.ltp} loLabel={fmtPx(lo20)} hiLabel={fmtPx(hi20)} gradient /></div>
    </div>
  );
}

export function trendNote(a: Analysed, hist: DailyCandle[]) {
  if (hist.length < 20) return '';
  const c20 = ((a.ltp - hist[hist.length - 20].c) / hist[hist.length - 20].c) * 100;
  return c20 > 3 ? 'Up-trend over the month' : c20 < -3 ? 'Down-trend over the month' : 'Sideways month';
}
